import { createTheme, ThemeProvider } from "@mui/material";
import { red } from "@mui/material/colors";
import * as React from "react";
import { Theme } from "@mui/material/styles";

interface ThemeProviderProps {
    isDark?: boolean;
    children?: React.ReactNode;
}

export interface ThemeData {
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
    fontFamily?: { fontFamily: string; src: string } | undefined;
}

// Module declaration should stay here and not in vendor.d.ts, as part of MUI v5 migration
declare module "@mui/styles" {
    interface DefaultTheme extends Theme {}
}

class ThemeProviderFactory {
    private themes = new Map<string, any>();

    get(themeData: ThemeData): React.FunctionComponent<ThemeProviderProps> {
        const stringThemeData = JSON.stringify(themeData);

        if (this.themes.has(stringThemeData)) {
            return this.themes.get(stringThemeData);
        }

        const newTheme = this.create(themeData);
        this.themes.set(stringThemeData, newTheme);
        return newTheme;
    }

    private create(themeData: ThemeData): React.FunctionComponent<ThemeProviderProps> {
        const lightTheme = createTheme({
            palette: {
                primary: {
                    main: themeData.primaryColor || spiffTheme.primaryColor,
                },
                secondary: {
                    main: themeData.secondaryColor || spiffTheme.secondaryColor,
                },
                error: red,
                mode: "light",
            },
            typography: {
                fontSize: 12,
                fontFamily: `${themeData.fontFamily?.fontFamily}, "Inter"`,
            },
        });

        const darkTheme = createTheme({
            palette: {
                primary: {
                    main: themeData.primaryColor || spiffTheme.primaryColor,
                },
                secondary: {
                    main: "#000000",
                },
                error: red,
                mode: "dark",
            },
            typography: {
                fontSize: 12,
                fontFamily: `${themeData.fontFamily?.fontFamily}, "Inter"`,
            },
        });

        return (props: ThemeProviderProps) => {
            return <ThemeProvider theme={props.isDark ? darkTheme : lightTheme}>{props.children}</ThemeProvider>;
        };
    }
}

export const themeProviderFactory = new ThemeProviderFactory();
export const spiffTheme = {
    primaryColor: "#c2c2c2",
    secondaryColor: "#ffffff",
    textColor: "#292929",
};
export const DefaultThemeProvider = themeProviderFactory.get(spiffTheme);
